import {
  Box,
  ButtonProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../assets/theme";
import { hasJwtPermission } from "../../services/ServicesHelpers";
import Actions from "../../components/common/Actions";
import CustomButton from "../../components/common/CustomButton";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";

import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

import CountryCodePhoneField from "../../components/CountryCodePhoneField";
import CustomCheckbox from "../../components/CustomCheckbox";

import { useQueryClient } from "@tanstack/react-query";
import ToastNotification from "../../components/ToastNotification";
import {
  createUser,
  fetchRoles,
  fetchUserByEmail,
  fetchUserHistory,
  getRoleOptions,
  getTeamLeaderOptions,
  sendReset,
  updateUser,
  UserType,
  useUsersQuery,
  useUsersByEmailQuery,
} from "../../services/UsersService";
import { IField, Params } from "../../types/table/interfaces.type";
import { countries } from "../../utils/countries";
import { extractPhoneNumberDetails } from "../../utils/phoneUtils";
import { GridAddIcon } from "@mui/x-data-grid";

import History from "../../components/History";
import AutoComplete, { IAutoCompleteProps } from "../../components/common/AutoComplete";
import TeamsDialog from "../../components/TeamsDialog";
import GroupsIcon from "@mui/icons-material/Groups";

interface Option {
  label: string;
  value: string;
  id?: string;
  name?: string;
  email?: string;
}

const UserPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [filters, setFilters] = useState<Params<UserType>>({});
  const [appliedFilters, setAppliedFilters] = useState<Params<UserType>>({
    search: "",
  });

  const [roles, setRoles] = useState<{ label: string; value: string }[]>([]);
  const [teamsDialogOpen, setTeamsDialogOpen] = useState(false);

  const dialogRef = useRef<UserDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "history" | "confirmPass",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  useEffect(() => {
    fetchRoles()
      .then((data) => {
        console.log("Fetched roles:", data.content);
        setRoles(
          data.content.map((role: { id: number; name: string }) => ({
            label: role.name.replace(/_/g, " "),
            value: role.id,
          }))
        );
      })
      .catch((error) => console.error("Error fetching roles:", error));
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      id: filters.name,
      email: filters.email,
      enabled: filters.enabled,
    }));
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: (row: any) => !hasJwtPermission("GET_USER") || row?.enabled === false,
    },
    onChangePassword: {
      handler: async (id: any) => {
        handleOpenDialog("confirmPass", String(id));
      },
      isDisabled: (row: any) => !hasJwtPermission("POST_USER_RESET"),
    },
    onViewHistory: {
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_USERS_HISTORY"),
    },
  });
  const { data: fetchUsersByEmail = [] } = useUsersByEmailQuery({ size: 10000 });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Name",
      value: filters.name ?? "",
      options: fetchUsersByEmail.map((option) => ({
        ...option,
        label: option.name,
        value: option.value,
      })),
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = fetchUsersByEmail.find(
          (option) => option.value === value
        );

        setFilters((prev) => ({
          ...prev,
          name: selectedOperator?.value,
        }));
      },
      clearable: true,
    },
    {
      label: "Email",
      value: filters.email ?? "",
      options: fetchUsersByEmail.map((option) => ({
        ...option,
        label: option.email,
        value: option.email,
      })),
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          email: String(value),
        }));
      },
      clearable: true,
    },
    {
      label: "Status",
      value: String(filters.enabled ?? ""),
      clearable: true,
      options: [
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,
          enabled:
            value === "true" ? true : value === "false" ? false : undefined,
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_USERS",
    },
  ];

  const fields: {
    key: keyof UserType;
    header: string;
    options?: (t: any) => any;
    transform?: (row: any) => any;
  }[] = [
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "phoneNumber", header: "Mobile No." },
    { key: "roleId", header: "Role", options: (t) => getRoleOptions(t.roleId) },
    {
      key: "teamLeaderId",
      header: "Team Leader",
      options: (t) => getTeamLeaderOptions(t.teamLeaderId),
    },
    {
      key: "enabled",
      header: "Status",
      transform: (row) => (row.enabled ? "Active" : "Inactive"),
    },
    { key: "createdDate", header: "Created Date" },
    { key: "lastLogin", header: "Last Login" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "flex-end", 
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <CustomButton
          size={isSmallScreen ? "medium" : "small"}
          variant="contained"
          label="Go To Role Permissions"
          onClick={() => navigate("/user_management/role")}
          textColor={COLORS.textPrimary}
          bgColor={COLORS.GOLD}
          sx={{
            minWidth: isSmallScreen ? "100%" : "auto",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            padding: { xs: "8px 16px", sm: "6px 16px" },
          }}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
      </Box>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="User List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              label: "Teams",
              variant: "outlined",
              startIcon: <GroupsIcon />,
              onClick: () => setTeamsDialogOpen(true),
              textColor: COLORS.typographyColor,
              borderColor: COLORS.GOLD,
              permission: "GET_ALL_TEAMS",
            },
            {
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_USER",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useUsersQuery}
          actions={actions}
          sortableFields={[
            "name",
            "email",
            "phoneNumber",
            "enabled",
            "lastLogin",
            "roleId",
            "createdDate",
          ]}
        />
      </Box>
      <UserDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        roles={roles}
        appliedFilters={appliedFilters}
      />
      <TeamsDialog
        open={teamsDialogOpen}
        onClose={() => setTeamsDialogOpen(false)}
      />
    </Box>
  );
};

export interface UserDialogRef {
  openDialog: (
    mode: "add" | "edit" | "history" | "confirmPass",
    id?: string
  ) => void;
}

interface UserDialogProps {
  onRefresh: () => void;
  roles: Option[];
  appliedFilters: Params<UserType>;
}

const UserDialog = forwardRef<UserDialogRef, UserDialogProps>(
  ({ onRefresh, roles, appliedFilters }, ref) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "history" | "confirmPass"
    >("add");
    const [dialogData, setDialogData] = useState<Partial<UserType>>({
      id: "",
      name: "",
      email: "",
      phoneNumber: "",
      roleId: "",
      enabled: true,
      twoFactorEmailEnabled: false,
      twoFactorTotpEnabled: false,
      twoFactorWhatsappEnabled: false,
      totpProvisionedAt: "",
    });
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    const { data: userOptions = [] } = useUsersByEmailQuery({ size: 10000 });
    const [currentLeaderOption, setCurrentLeaderOption] = useState<{
      label: string;
      value: string;
    } | null>(null);

    const teamLeaderOptions = useMemo(() => {
      const options = userOptions
        .filter((option) => option.value !== dialogData.id)
        .map((option) => ({
          label: option.email ? `${option.name} (${option.email})` : option.name,
          value: option.value,
        }));

      if (
        currentLeaderOption &&
        !options.some((option) => option.value === currentLeaderOption.value)
      ) {
        options.push(currentLeaderOption);
      }
      return options;
    }, [userOptions, dialogData.id, currentLeaderOption]);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setDialogOpen(true);
        setSelectedId(id);
        openDialog(mode, id);
      },
    }));

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    const formatPhoneNumber = (number: string, countryCode: string) => {
      if (!number) return "";
      try {
        const parsedNumber = parsePhoneNumberFromString(
          number,
          countryCode as CountryCode
        );
        return parsedNumber ? parsedNumber.nationalNumber : number;
      } catch {
        return number;
      }
    };

    useEffect(() => {
      const isAnyFieldEmpty =
        !dialogData.name ||
        !dialogData.email ||
        !dialogData.phoneNumber ||
        !dialogData.roleId;
      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const closeDialog = () => {
      setDialogOpen(false);
      setDialogData({});
    };

    const openDialog = async (
      mode: "add" | "edit" | "history" | "confirmPass",
      id?: string
    ) => {
      setDialogMode(mode);
      if (mode === "edit" && id) {
        try {
          const userData = await fetchUserByEmail(id);
          const { countryCode, phoneNumber } = extractPhoneNumberDetails(
            userData.phoneNumber || "",
            [...countries]
          );

          setDialogData({
            id: userData.id || "",
            name: userData.name || "",
            email: userData.email || "",
            phoneNumber: phoneNumber || "",
            countryCode: countryCode || "",
            roleId: userData.roleId || "",
            teamLeaderId: userData.teamLeaderId ?? null,
            enabled: userData.enabled ?? false,
            twoFactorEmailEnabled: userData.twoFactorEmailEnabled ?? false,
            twoFactorTotpEnabled: userData.twoFactorTotpEnabled ?? false,
            twoFactorWhatsappEnabled: userData.twoFactorWhatsappEnabled ?? false,
            totpProvisionedAt: userData.totpProvisionedAt || "",
            createdDate: userData.createdDate || "",
            lastLogin: userData.lastLogin || "",
          });

          if (countryCode) {
            setDialogData((prev) => ({
              ...prev,
              countryCode,
            }));
          }

          setCurrentLeaderOption(null);
          if (userData.teamLeaderId) {
            try {
              const leader = await fetchUserByEmail(userData.teamLeaderId);
              setCurrentLeaderOption({
                label: leader.email
                  ? `${leader.name} (${leader.email})`
                  : leader.name,
                value: leader.id,
              });
            } catch (error: any) {
              console.error("Error fetching the team leader:", error);
            }
          }
        } catch (error: any) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setCurrentLeaderOption(null);
        setDialogData({
          name: "",
          phoneNumber: "",
          countryCode: "",
          email: "",
          roleId: "",
          teamLeaderId: null,
          enabled: false,
          twoFactorEmailEnabled: false,
          twoFactorTotpEnabled: false,
          twoFactorWhatsappEnabled: false,
          totpProvisionedAt: "",
          createdDate: "",
          lastLogin: "",
        });
      }
    };

    const handleResetPassword = async () => {
      try {
        await sendReset(selectedId || "");
        closeDialog();
        onRefresh();
        ToastNotification.success("Password reset link sent successfully!");
      } catch (error: any) {
        console.error("Error sending reset password:", error);
      }
    };

    const addUser = async (userData: Partial<UserType>) => {
      try {
        console.log("Country code:", userData);
        const numericCode = getCountryCallingCode(
          userData.countryCode as CountryCode
        );
        const fullPhoneNumber = `+${numericCode}${userData.phoneNumber || ""}`;
        await createUser({
          ...userData,
          phoneNumber: fullPhoneNumber,
          teamLeaderId: userData.teamLeaderId || null,
        });
        onRefresh();
        closeDialog();
        ToastNotification.success("User added successfully!");
      } catch (error: any) {
        console.error("Error adding user:", error);
        ToastNotification.error(error?.message || "Failed to add the user.");
      }
    };

    const editUser = async (userData: Partial<UserType>) => {
      try {
        const numericCode = getCountryCallingCode(
          userData.countryCode as CountryCode
        );
        const fullPhoneNumber = `+${numericCode}${userData.phoneNumber || ""}`;
        await updateUser({
          ...userData,
          phoneNumber: fullPhoneNumber,
          teamLeaderId: userData.teamLeaderId || null,
        });
        onRefresh();
        closeDialog();
        ToastNotification.success("User Updated successfully!");
      } catch (error: any) {
        console.error("Error updating user:", error);
        ToastNotification.error(error?.message || "Failed to update the user.");
      }
    };

    const fieldHistory: IField<UserType & { historyTimestamp: Date }>[] = [
      {
        key: "name",
        header: "Name",
      },
      {
        key: "email",
        header: "Email",
      },
      {
        key: "phoneNumber",
        header: "Mobile No.",
      },
      {
        key: "roleId",
        header: "Role",
        options: (t) => getRoleOptions(t.roleId),
      },
      {
        key: "teamLeaderId",
        header: "Team Leader",
        options: (t) => getTeamLeaderOptions(t.teamLeaderId),
      },
      {
        key: "enabled",
        header: "Status",
        transform: (row) => (row.enabled ? "Active" : "Inactive"),
      },
      {
        key: "createdDate",
        header: "Created Date",
      },
      {
        key: "lastLogin",
        header: "Last Login",
      },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
    ];
    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={closeDialog}
          mode={dialogMode}
          title={
            dialogMode === "confirmPass"
              ? "Reset Password"
              : dialogMode === "history"
              ? "History"
              : dialogMode === "add"
              ? "Add User"
              : "Edit User"
          }
          fields={
            dialogMode === "confirmPass"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>Are you sure you want to reset the password?</Box>
                    ),
                  },
                ]
              : dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History
                        filters={appliedFilters}
                        fields={fieldHistory}
                        fetchFunc={async ({ size = 10, ...rest }) => {
                          const result = await fetchUserHistory(selectedId || "", {
                            ...rest,
                            size,
                          });
                          return {
                            ...result,
                            content: result.content.map((item: any) => ({
                              ...item,
                              historyTimestamp: item.historyTimestamp,
                            })),
                          };
                        }}
                      />
                    ),
                  },
                ]
              : [
                  {
                    type: "text",
                    label: "User Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name || "",
                    onChange: (value) => {
                      setDialogData((prev) => ({
                        ...prev,
                        name: String(value),
                      }));
                      validateField("name", String(value));
                    },
                    placeholder: "Enter Name",
                    error: !!errors.name,
                    helperText: errors.name ? "Name is required" : "",
                  },
                  {
                    type: "text",
                    label: "Email",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.email || "",
                    validateOnChange: true,
                    typeinput: "email",
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        email: isError,
                      }));
                    },
                    helperText: errors.email ? "Invalid email format." : "",
                    onChange: (value) => {
                      const trimmedValue = String(value).trimStart();
                      setDialogData((prev) => ({
                        ...prev,
                        email: trimmedValue,
                      }));
                    },
                    placeholder: "Enter Email",
                    error: !!errors.email,
                    disabled: dialogMode === "edit",
                  },
                  {
                    type: "custom",
                    component: (

                      <CountryCodePhoneField
                        countries={countries}
                        countryCode={dialogData.countryCode || ""}
                        phoneNumber={dialogData.phoneNumber || ""}
                        onCountryCodeChange={(newVal: string) => {
                          setDialogData((prev) => ({
                            ...prev,
                            countryCode: newVal,
                          }));
                          validateField("countryCode", newVal);

                          if (dialogData.phoneNumber?.trim()) {
                            const reFormatted = formatPhoneNumber(
                              dialogData.phoneNumber,
                              newVal
                            );
                            setDialogData((prev) => ({
                              ...prev,
                              phoneNumber: reFormatted,
                            }));
                            validateField("phoneNumber", reFormatted);
                          }
                        }}
                        onPhoneNumberChange={(newVal: string) => {
                          const trimmedValue = newVal.trim();
                          const formattedNumber = formatPhoneNumber(
                            trimmedValue,
                            dialogData.countryCode || ""
                          );

                          setDialogData((prev) => ({
                            ...prev,
                            phoneNumber: formattedNumber,
                          }));
                          validateField("phoneNumber", formattedNumber);

                          if (trimmedValue) {
                            const parsedNumber = parsePhoneNumberFromString(
                              trimmedValue,
                              (dialogData.countryCode || "") as CountryCode
                            );
                            setErrors((prev) => ({
                              ...prev,
                              phoneNumber: !parsedNumber?.isValid(),
                            }));
                          } else {
                            setErrors((prev) => ({
                              ...prev,
                              phoneNumber: false,
                            }));
                          }
                        }}
                        error={errors.phoneNumber}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Role"
                        highlightLabelStar={true}
                        value={dialogData.roleId || ""}
                        options={roles}
                        onChange={(value) => {
                          setDialogData((prev) => ({
                            ...prev,
                            roleId: Array.isArray(value)
                              ? value.join(",")
                              : String(value),
                          }));
                          validateField("roleId", String(value));
                        }}
                        clearable={true}
                        error={!!errors.roleId}
                        placeholder="Select Role"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Team Leader"
                        value={dialogData.teamLeaderId ?? ""}
                        options={teamLeaderOptions}
                        disabled={dialogMode === "edit" && !dialogData.enabled}
                        helperText={
                          dialogMode === "edit" && !dialogData.enabled
                            ? "Inactive users cannot be on a team."
                            : ""
                        }
                        onChange={(value) => {
                          const selected = Array.isArray(value)
                            ? value[0]
                            : value;
                          setDialogData((prev) => ({
                            ...prev,
                            teamLeaderId: selected ? String(selected) : null,
                          }));
                        }}
                        clearable={true}
                        placeholder="Select Team Leader"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  ...(dialogMode === "edit"
                    ? [
                        {
                          type: "switch" as const,
                          label: "Enabled",
                          value: dialogData.enabled || false,
                          onChange: (value: any) => {
                            const enabled = Boolean(value);
                            setDialogData((prev) => ({
                              ...prev,
                              enabled,
                              // An inactive user leaves their team.
                              teamLeaderId: enabled
                                ? prev.teamLeaderId ?? null
                                : null,
                            }));
                          },
                        },
                        {
                          type: "custom" as const,
                          component: (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start", width: "100%" }}>
                              <CustomLabel
                                text="Two Factor Authentication"
                                fontWeight={500}
                                fontSize="0.95rem"
                                sx={{ margin: 0, mb: 0.5, textAlign: "left" }}
                              />
                              <CustomCheckbox
                                checked={dialogData.twoFactorEmailEnabled || false}
                                onChange={(e) =>
                                  setDialogData((prev) => ({
                                    ...prev,
                                    twoFactorEmailEnabled: e.target.checked,
                                  }))
                                }
                                label="Email"
                              />
                              <CustomCheckbox
                                checked={dialogData.twoFactorWhatsappEnabled || false}
                                onChange={(e) =>
                                  setDialogData((prev) => ({
                                    ...prev,
                                    twoFactorWhatsappEnabled: e.target.checked,
                                  }))
                                }
                                label="WhatsApp"
                              />
                              <CustomCheckbox
                                checked={dialogData.twoFactorTotpEnabled || false}
                                onChange={(e) =>
                                  setDialogData((prev) => ({
                                    ...prev,
                                    twoFactorTotpEnabled: e.target.checked,
                                  }))
                                }
                                label="Authenticator"
                              />
                            </Box>
                          ),
                        },
                      ]
                    : []),
                ]
          }
          buttons={
            dialogMode === "confirmPass"
              ? [
                  {
                    label: "Reset",
                    variant: "contained",
                    bgColor: COLORS.GOLD,
                    borderRadius: "12px",
                    onClick: handleResetPassword,
                    permission: "POST_USER_RESET",
                  },
                ]
              : dialogMode === "history"
              ? []
              : [
                  {
                    label: "Save",
                    variant: "contained",
                    bgColor: COLORS.GOLD,
                    borderRadius: "12px",
                    onClick: async () => {
                      if (dialogMode === "edit") {
                        await editUser(dialogData);
                      } else {
                        await addUser(dialogData);
                      }
                    },
                    disabled:
                      isSaveDisabled ||
                      (dialogMode === "edit"
                        ? !hasJwtPermission("PUT_USER")
                        : !hasJwtPermission("POST_USER")),
                  },
                ]
          }
        />
      </>
    );
  }
);

export default UserPage;
